import React from 'react';
import { Plan, PricingVariant } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { isRecurringVariant } from '@wix/pricing-plans-utils';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { NO_BREAK_SPACE } from '../../../../../constants';
import { ButtonDemo } from '../../../../../constants/settings-events';
import { useAdaptedSettings } from '../../../../../hooks';
import { useGoToCheckoutLoading } from '../../../../../hooks/useGoToCheckoutLoading';
import { getVariantDuration } from '../../../../../utils/plan';
import { VariantCategoryNameById } from '../../../../../utils/variant-categories';
import { VariantsByPlan } from '../../../../../utils/variant-visibility';
import { orderPlansForMobileView } from '../../../utils/order-plans-for-mobile-view';
import { MAX_ITEM_WIDTH } from '../PlanList';
import { PlanCard } from './PlanCard';
import { classes } from './PlanCardGrid.st.css';

export interface PlanCardGridProps {
  plans: Plan[];
  variantEntries?: VariantsByPlan;
  variantCategoryNameById: VariantCategoryNameById;
  isVariantSelectionEnabled: boolean;
  isTabsView?: boolean;
  showDemoHighlight?: boolean;
  highlightedDemoPlanId?: string;
  buttonDemo: ButtonDemo;
  expandMobileBenefits: boolean;
  selectPlan(plan: Plan, variant: PricingVariant): void;
  addToCart(plan: Plan, variant: PricingVariant): void;
  resetLoadingState?(): void;
  selectedPlanId?: string;
  isOnEcom: boolean;
  isAddToCartLoading?: boolean;
}

export function getVisibleVariants({
  plan,
  variantEntries,
}: {
  plan: Plan;
  variantEntries?: VariantsByPlan;
}): PricingVariant[] {
  // Without variantEntries (variants experiment off or failed to load), each plan shows only its first variant.
  return variantEntries ? variantEntries[plan.id!] ?? [] : plan.pricingVariants!.slice(0, 1);
}

export const PlanCardGrid: React.FC<PlanCardGridProps> = ({
  plans,
  variantEntries,
  variantCategoryNameById,
  isVariantSelectionEnabled,
  isTabsView = false,
  showDemoHighlight,
  highlightedDemoPlanId,
  buttonDemo,
  expandMobileBenefits,
  selectPlan,
  addToCart,
  resetLoadingState,
  selectedPlanId,
  isOnEcom,
  isAddToCartLoading,
}) => {
  const { isGoToCheckoutLoading, setIsGoToCheckoutLoading } = useGoToCheckoutLoading({ isOnEcom, resetLoadingState });
  const settings = useAdaptedSettings();
  const { t } = useTranslation();
  const { isMobile, isEditorX } = useEnvironment();

  const highlightedPlanId = settings.getHighlightedPlanId(plans, showDemoHighlight, highlightedDemoPlanId);
  const highlightedVariant = settings.get('highlightedVariant');
  const orderedPlans = isMobile && highlightedPlanId ? orderPlansForMobileView(plans, highlightedPlanId) : plans;
  const visiblePlans = orderedPlans.filter((plan) => getVisibleVariants({ plan, variantEntries }).length);

  const allVariants = visiblePlans.flatMap((plan) => getVisibleVariants({ plan, variantEntries }));
  // TODO: the *Exist flags below only force row alignment; remove them now that rows align with subgrid.
  const recurringPlansExist = allVariants.some(isRecurringVariant);
  const freeTrialDaysExist = allVariants.some((variant) => !!variant.freeTrialDays);
  const setupFeesExist = allVariants.some((variant) => !!variant.fees?.length);
  const validityCyclesExist = allVariants.some((variant) => getVariantDuration({ variant, t }) !== NO_BREAK_SPACE);
  const taglinesExist = visiblePlans.some((plan) => !!plan.description);
  const anyBenefits = visiblePlans.some((plan) => !!plan.perks?.length);

  const getIsHighlighted = (plan: Plan) => {
    if (plan.id !== highlightedPlanId) {
      return false;
    }

    if (isTabsView && highlightedVariant) {
      return getVisibleVariants({ plan, variantEntries }).some((variant) =>
        variant.categoryIds?.includes(highlightedVariant),
      );
    }

    return true;
  };

  const onBuyNowClick = (plan: Plan, variant: PricingVariant) => {
    setIsGoToCheckoutLoading(true);
    selectPlan(plan, variant);
  };

  return (
    <ul className={classes.container} style={isEditorX ? {} : { maxWidth: MAX_ITEM_WIDTH * visiblePlans.length }}>
      {visiblePlans.map((plan) => (
        <PlanCard
          key={plan.id}
          plan={plan}
          variants={getVisibleVariants({ plan, variantEntries })}
          variantCategoryNameById={variantCategoryNameById}
          isVariantSelectionEnabled={isVariantSelectionEnabled}
          highlighted={getIsHighlighted(plan)}
          buttonDemo={buttonDemo}
          recurringPlansExist={recurringPlansExist}
          freeTrialDaysExist={freeTrialDaysExist}
          validityCyclesExist={validityCyclesExist}
          setupFeesExist={setupFeesExist}
          taglinesExist={taglinesExist}
          anyBenefits={anyBenefits}
          expandMobileBenefits={expandMobileBenefits}
          isGoToCheckoutLoading={isGoToCheckoutLoading && plan.id === selectedPlanId}
          isAddToCartLoading={isAddToCartLoading && plan.id === selectedPlanId}
          isOnEcom={isOnEcom}
          onBuyNowClick={(variant) => onBuyNowClick(plan, variant)}
          onAddToCartClick={(variant) => addToCart(plan, variant)}
        />
      ))}
    </ul>
  );
};
